import React, { useState } from "react";
import { useApi } from "../hooks/useApi";

const card = { background: "#0d1224", border: "1px solid #1e2a45", borderRadius: "10px", padding: "14px" };

const GATE_LABELS = {
  calendar:    { icon: "📅", label: "Calendar" },
  vix:         { icon: "😨", label: "VIX" },
  roro:        { icon: "📊", label: "RORO" },
  polymarket:  { icon: "🎯", label: "Crowd" },
  sentiment:   { icon: "📰", label: "Sentiment" },
  correlation: { icon: "🔗", label: "Correlation" },
  ai:          { icon: "🧠", label: "AI" },
};

function GateDot({ pass, reason }) {
  return (
    <div title={reason} style={{
      width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0,
      background: pass ? "#22c55e" : "#ef4444",
      boxShadow: pass ? "0 0 4px #22c55e" : "0 0 4px #ef4444",
      cursor: "help",
    }} />
  );
}

function humanizeCorrelation(reason) {
  if (!reason || reason === "OK") return null;
  // CORR_SAME_AUDUSD_WTICOUSD_BUY → "AUDUSD BUY blocked — same macro bet as WTICOUSD"
  const m = reason.match(/CORR_SAME_(\w+)_(\w+)_(\w+)/);
  if (m) return `${m[1]} ${m[3]} blocked — same macro bet as open ${m[2]}`;
  const m2 = reason.match(/CORR_OPPOSITE_(\w+)_(\w+)/);
  if (m2) return `${m2[1]} blocked — contradicts open ${m2[2]}`;
  return reason;
}

export default function GateStatus() {
  const { data, loading } = useApi("/intelligence", 15000);
  const [expanded, setExpanded] = useState(null);
  const symbols = data?.symbols || [];

  return (
    <div style={card}>
      <div style={{ fontWeight: 600, color: "#94a3b8", marginBottom: "10px", fontSize: "13px" }}>
        🚦 Decision Gates
      </div>

      {loading && <div style={{ color: "#475569", fontSize: "12px" }}>Loading...</div>}

      {/* Gate legend */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "8px", flexWrap: "wrap" }}>
        {Object.entries(GATE_LABELS).map(([key, { icon, label }]) => (
          <div key={key} style={{ fontSize: "9px", color: "#475569", display: "flex", alignItems: "center", gap: "2px" }}>
            {icon} {label}
          </div>
        ))}
      </div>

      {/* Per-symbol gate rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {symbols.map((s) => {
          const allPass = !s.entry_blocked;
          const isOpen  = expanded === s.symbol;
          const corrGate = s.gates?.correlation;
          const corrBlocked = corrGate && !corrGate.pass;

          return (
            <div key={s.symbol}>
              <div
                onClick={() => setExpanded(isOpen ? null : s.symbol)}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "5px 8px", borderRadius: "6px", cursor: "pointer",
                  background: isOpen ? "#111827" : corrBlocked ? "#1a0a0a" : "transparent",
                  border: `1px solid ${allPass ? "#14532d" : corrBlocked ? "#7f1d1d" : "#1e2a45"}`,
                }}
              >
                <span style={{ fontSize: "11px", fontWeight: 700, color: "#e2e8f0", width: "68px" }}>
                  {s.symbol}
                </span>

                {/* Gate dots */}
                {Object.keys(GATE_LABELS).map((key) => (
                  <GateDot key={key}
                    pass={s.gates?.[key]?.pass ?? true}
                    reason={s.gates?.[key]?.reason ?? ""}
                  />
                ))}

                {/* AI signal badge */}
                <span style={{
                  marginLeft: "auto", fontSize: "10px", fontWeight: 700, padding: "1px 6px",
                  borderRadius: "4px",
                  background: s.ai_action === "BUY" ? "#166534" : s.ai_action === "SELL" ? "#7f1d1d" : "#1e2a45",
                  color: s.ai_action === "BUY" ? "#4ade80" : s.ai_action === "SELL" ? "#f87171" : "#64748b",
                }}>
                  {s.ai_action} {s.ai_confidence > 0 ? `${(s.ai_confidence*100).toFixed(0)}%` : ""}
                </span>

                <span style={{ fontSize: "9px", color: allPass ? "#22c55e" : "#ef4444" }}>
                  {allPass ? "✓" : "✗"}
                </span>
              </div>

              {/* Correlation block banner */}
              {corrBlocked && !isOpen && (
                <div style={{
                  padding: "3px 8px", fontSize: "9px", color: "#fca5a5",
                  background: "#1a0505", borderLeft: "2px solid #ef4444",
                  marginTop: "1px", borderRadius: "0 0 4px 4px",
                }}>
                  🔗 {humanizeCorrelation(corrGate.reason)}
                </div>
              )}

              {/* Expanded gate detail */}
              {isOpen && (
                <div style={{ padding: "6px 8px", background: "#080c18", borderRadius: "0 0 6px 6px",
                              border: "1px solid #1e2a45", borderTop: "none" }}>
                  {Object.entries(GATE_LABELS).map(([key, { icon, label }]) => {
                    const g = s.gates?.[key];
                    const isCorr = key === "correlation" && g && !g.pass;
                    return (
                      <div key={key} style={{
                        display: "flex", justifyContent: "space-between",
                        fontSize: "10px", padding: "3px 0",
                        borderBottom: "1px solid #0f172a",
                        background: isCorr ? "#1a0505" : "transparent",
                      }}>
                        <span style={{ color: "#64748b" }}>{icon} {label}</span>
                        <span style={{
                          color: g?.pass ? "#4ade80" : "#f87171",
                          maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {key === "correlation" && !g?.pass
                            ? humanizeCorrelation(g?.reason)
                            : g?.reason}
                        </span>
                      </div>
                    );
                  })}
                  {s.sl > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", paddingTop: "4px" }}>
                      <span style={{ color: "#64748b" }}>SL / TP</span>
                      <span style={{ color: "#94a3b8" }}>{s.sl?.toFixed(5)} / {s.tp?.toFixed(5)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
